import { EnumTipoDomandaModuloDTOTipoAssociazione } from '@framework/api-client';
import { t } from '@framework/i18n';
import { useMemo } from 'react';
import { ITriggerableDifferences } from './useTriggerableModulesConfirmUpdate';

interface Props {
  differences: ITriggerableDifferences[];
  content: string;
}

function getAddedLabel(length: number) {
  if (length === 1) {
    return t(
      'confirm-update-modal.added-new-module-activation',
      `È stata aggiunta una nuova attivazione modulo:`,
    );
  }
  return t(
    'confirm-update-modal.added-new-multiple-modules-activation',
    `Sono state aggiunte {{length}} nuove attivazioni moduli:`,
    { length },
  );
}

function getRemovedLabel(length: number) {
  if (length === 1) {
    return t(
      'confirm-update-modal.removed-module-activation',
      `È stata rimossa una attivazione modulo:`,
    );
  }
  return t(
    'confirm-update-modal.removed-multiple-modules-activation',
    `Sono state rimosse {{length}} attivazioni moduli:`,
    { length },
  );
}

/**
 * Modal che mostra il riepilogo delle modifiche apportate alle domande
 * una volta confermata l'update di un modulo/sezione. Le modifiche sono
 * fornite dal BE e riguardano l'aggiunta/rimozione di moduli dinamici.
 */
export function ConfirmUpdateModalContent(props: Props) {
  const { differences, content } = props;

  const { added, removed, inquiries } = useMemo(() => {
    const triggerableDifferences = differences[0];

    if (!triggerableDifferences) {
      console.warn("[TriggerableModulesModalContent] Nessuna differenza trovata", { differences }); // prettier-ignore
      return { added: [], removed: [], inquiries: [] };
    }

    const added = triggerableDifferences.moduli.filter(
      m =>
        m.stato === 'AGGIUNTO' &&
        m.tipo === EnumTipoDomandaModuloDTOTipoAssociazione.DINAMICO,
    );
    const removed = triggerableDifferences.moduli.filter(
      m =>
        m.stato === 'RIMOSSO' &&
        m.tipo === EnumTipoDomandaModuloDTOTipoAssociazione.DINAMICO,
    );

    const inquiries = differences.map(d => d.titolo);

    return { added, removed, inquiries };
  }, [differences]);

  return (
    <>
      {added.length > 0 && (
        <>
          <p>{getAddedLabel(added.length)}</p>
          <ul>
            {added.map(module => (
              <li key={module.idModulo}>{module.titolo}</li>
            ))}
          </ul>
        </>
      )}
      {removed.length > 0 && (
        <>
          <p>{getRemovedLabel(removed.length)}</p>
          <ul>
            {removed.map(module => (
              <li key={module.idModulo}>{module.titolo}</li>
            ))}
          </ul>
        </>
      )}
      {t(
        'confirm-update-modal.changes-will-be-applied-to-inquiries',
        'Le modifiche saranno apportate a {{inquiriesLength}} domand{{oneOrMore}}:',
        {
          oneOrMore: inquiries.length === 1 ? 'a' : 'e',
          inquiriesLength: inquiries.length,
        },
      )}
      <ul>
        {inquiries.map(inquiry => (
          <li key={inquiry}>{inquiry}</li>
        ))}
      </ul>
      <p>{content}</p>
    </>
  );
}
